import { useState, useRef } from 'react';

function getInitial(emailStr = '') {
  const clean = emailStr.trim().replace(/^["']|["']$/g, '');
  const match = clean.match(/^(.*?)\s*<[^>]+>/);
  const displayName = match ? match[1].trim() : clean;
  const initial = (displayName[0] || 'U').toUpperCase();
  return initial;
}

export function EmailChipInput({
  value = '',
  onChange,
  placeholder = 'Recipient email address...',
  readOnly = false,
  autoFocus = false,
  className = '',
  inputClassName = '',
}) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  // Normalize incoming value to an array of email strings
  const isStringValue = typeof value === 'string';
  const emailList = isStringValue
    ? value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : Array.isArray(value)
      ? value.filter(Boolean)
      : [];

  const updateEmails = (newList) => {
    if (!onChange) return;
    if (isStringValue) {
      onChange(newList.join(', '));
    } else {
      onChange(newList);
    }
  };

  const addEmails = (rawText) => {
    if (!rawText || !rawText.trim()) return;
    const itemsToAdd = rawText
      .split(/[,;\s\n\r]+/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (itemsToAdd.length === 0) return;

    // Filter duplicates if preferred or append all
    const updated = [...emailList];
    itemsToAdd.forEach((item) => {
      if (!updated.includes(item)) {
        updated.push(item);
      }
    });

    updateEmails(updated);
    setInputValue('');
  };

  const removeEmail = (indexToRemove) => {
    const updated = emailList.filter((_, idx) => idx !== indexToRemove);
    updateEmails(updated);
  };

  const handleKeyDown = (e) => {
    if (
      ['Enter', ',', 'Tab'].includes(e.key) ||
      (e.key === ' ' && inputValue.trim())
    ) {
      e.preventDefault();
      addEmails(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && emailList.length > 0) {
      removeEmail(emailList.length - 1);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addEmails(inputValue);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    if (pastedData) {
      addEmails(pastedData);
    }
  };

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className={`flex flex-wrap items-center gap-1.5 min-h-[28px] w-full py-0.5 cursor-text ${className}`}
    >
      {emailList.map((email, idx) => {
        const initial = getInitial(email);
        return (
          <div
            key={`${email}-${idx}`}
            className="inline-flex items-center gap-1.5 h-7 px-2.5 max-w-full rounded-lg border border-border-default bg-white text-xs font-medium text-text-primary shadow-2xs hover:border-primary/50 hover:bg-primary/5 transition-all select-none group shrink-0"
          >
            <div className="w-4 h-4 rounded-full bg-primary/15 text-primary border border-primary/20 flex items-center justify-center font-bold text-[9px] shrink-0 uppercase">
              {initial}
            </div>
            <span className="truncate max-w-[150px] xs:max-w-[200px] sm:max-w-[260px] text-text-primary text-xs font-medium">
              {email}
            </span>
            {!readOnly && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeEmail(idx);
                }}
                className="text-text-secondary hover:text-status-error hover:bg-red-50 p-0.5 rounded-md transition-colors shrink-0 outline-none cursor-pointer"
                title="Remove"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        );
      })}

      {!readOnly && (
        <input
          ref={inputRef}
          type="email"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
          placeholder={emailList.length === 0 ? placeholder : ''}
          autoFocus={autoFocus}
          className={`flex-1 min-w-[80px] sm:min-w-[120px] h-7 outline-none border-none bg-transparent text-xs text-text-primary placeholder:text-text-secondary ${inputClassName}`}
        />
      )}
    </div>
  );
}
